/**
 * 個別説明の候補 (Deal.briefingCandidates) / セッション (Deal.briefingSessions) の
 * 読み出しヘルパー。2026-09-09 に admin-web (BriefingCandidateDecisionDialog) から移設。
 *
 * フロントエンド (個別説明ページ / 商談詳細) と Cloud Functions
 * (getBriefingSubmissionsForSa: 外部SA への申込内容の開示判定) の両方から参照する。
 * 「自分に関係する申込か」の判定が 2 箇所で別々に実装されて食い違うことを防ぐため、
 * 判定ロジックは必ずここに集約する。
 */
import type { BriefingCandidateEntry, BriefingSessionEntry, Deal } from '../types/deal';

/**
 * Deal から候補エントリを取り出す (旧 briefingCandidateScheduleIds は pending 昇格)。
 */
export function dealBriefingCandidates(
  deal: Pick<Deal, 'briefingCandidates' | 'briefingCandidateScheduleIds'>,
): BriefingCandidateEntry[] {
  if (deal.briefingCandidates && deal.briefingCandidates.length > 0) {
    return deal.briefingCandidates;
  }
  return (deal.briefingCandidateScheduleIds ?? []).map((sid) => ({
    scheduleId: sid,
    formSubmissionId: null,
    status: 'pending' as const,
    decidedAt: null,
    createdAt: '',
  }));
}

/**
 * 確定済みの個別説明セッション一覧 (2026-08-18)。
 * briefingSessions が空の旧データは、単一フィールド (confirmedBriefingScheduleId +
 * scheduledBriefingAt) から 1 件のセッションとして昇格する。
 */
export function dealBriefingSessions(
  deal: Pick<
    Deal,
    | 'briefingSessions'
    | 'confirmedBriefingScheduleId'
    | 'scheduledBriefingAt'
    | 'briefingAssigneeUid'
    | 'briefingParticipantCount'
    | 'confirmedBriefingStyle'
  >,
): BriefingSessionEntry[] {
  if (deal.briefingSessions && deal.briefingSessions.length > 0) {
    return deal.briefingSessions;
  }
  if (deal.scheduledBriefingAt) {
    return [
      {
        scheduleId: deal.confirmedBriefingScheduleId ?? '',
        scheduledAt: deal.scheduledBriefingAt,
        assigneeUid: deal.briefingAssigneeUid ?? null,
        participantCount: Math.max(1, deal.briefingParticipantCount ?? 1),
        style: deal.confirmedBriefingStyle ?? null,
        formSubmissionId: null,
        confirmedAt: deal.scheduledBriefingAt,
        confirmedBy: 'system',
      },
    ];
  }
  return [];
}

/** pending の候補のみ。 */
export function dealPendingBriefingCandidates(
  deal: Pick<Deal, 'briefingCandidates' | 'briefingCandidateScheduleIds'>,
): BriefingCandidateEntry[] {
  return dealBriefingCandidates(deal).filter((c) => c.status === 'pending');
}

/** dealBriefingCandidates / dealBriefingSessions の両方に必要な Deal のフィールド。 */
export type BriefingVisibilityDeal = Pick<
  Deal,
  | 'briefingCandidates'
  | 'briefingCandidateScheduleIds'
  | 'briefingSessions'
  | 'confirmedBriefingScheduleId'
  | 'scheduledBriefingAt'
  | 'briefingAssigneeUid'
  | 'briefingParticipantCount'
  | 'confirmedBriefingStyle'
>;

/**
 * 外部SA が閲覧してよい申込 (form_submissions) の id 集合 (2026-09-09)。
 *
 * 開示は「申込 (formSubmissionId) 単位」で判定する。Deal 単位で一括開示しない。
 * ある申込について、SA が次のいずれかに該当するときのみ閲覧可:
 *   - その申込に対する pending 候補 (briefingCandidates で formSubmissionId が一致) の
 *     scheduleId が、自分が担当する briefing_schedules (assigneeUid === 自分) と一致する
 *   - その申込に対する確定済みセッション (briefingSessions で formSubmissionId が一致) の
 *     assigneeUid が自分と一致する
 *
 * formSubmissionId が無い旧データ (briefingCandidateScheduleIds のみ / 手動確定セッション)
 * は対象外 (フォールバックしない)。
 *
 * 確定すると同一申込グループの他 pending は cancelled になる (applyConfirmDecision) ため、
 * 候補から外れた他SA は自動的に閲覧不可になる。
 */
export function visibleBriefingSubmissionIdsForSa(
  deal: BriefingVisibilityDeal,
  args: {
    /** 判定対象の外部SA uid */
    saUid: string;
    /** その SA が担当する briefing_schedules の id 集合 */
    mySlotIds: ReadonlySet<string>;
  },
): Set<string> {
  const { saUid, mySlotIds } = args;
  const out = new Set<string>();
  if (!saUid) return out;
  for (const c of dealPendingBriefingCandidates(deal)) {
    if (c.formSubmissionId && mySlotIds.has(c.scheduleId)) {
      out.add(c.formSubmissionId);
    }
  }
  for (const s of dealBriefingSessions(deal)) {
    if (s.formSubmissionId && s.assigneeUid === saUid) {
      out.add(s.formSubmissionId);
    }
  }
  return out;
}
